import { useState, useEffect, useCallback } from 'react';
import { NotificationToast } from '../../../../components/organisms/notification-toast';
import Modal, { ModalProps } from '../../../../components/template/mvc-modal';
import Toggle from '../../../../components/molecules/toggle';
import { useTranslation } from 'react-i18next';
import { EditableChangeEvent } from '../../../../utils/types/editable';
import Input from '../../../../components/atoms/input';
import ClientService, { IClient } from '../../../../services/Client.service';
import { useRouteLoaderData } from 'react-router-dom';
import Select from '../../../../components/atoms/select';
import { IUser } from '../../../../services/User.service';
import MaterialService, {
	IMaterial,
} from '../../../../services/Material.service';
import { HomeLoaderData } from '../../../../types/homeLoaderData';

export interface MaterialModalProps extends ModalProps<IMaterial> {
	setModalProps: React.Dispatch<React.SetStateAction<ModalProps<IMaterial>>>;
}

const MaterialsModal = ({
	data,
	setModalProps,
	onClose,
	mode,
	isModalVisible,
	...props
}: MaterialModalProps) => {
	const { t } = useTranslation();

	const { accountId } = useRouteLoaderData('home') as HomeLoaderData;

	const [error, setError] = useState<string>('');

	useEffect(() => {
		if (!error) return;
		NotificationToast.Warn(t(error));
		setError('');
	}, [error]);

	const onSubmit = () => {
		if (!validate()) return;
		if (mode === 'edit') {
			onEdit();
			return;
		}
		onCreate();
	};

	const validate = (): boolean => {
		if (!data.name || !data.value || !data.price) {
			setError('alert.error.mandatory');
			return false;
		}
		return true;
	};

	const onCreate = async () => {
		const response = await MaterialService.create(
			{ ...data, client: { id: Number(accountId) } },
			setError
		);
		if (!response) return;
		NotificationToast.Success(
			`${t('port_management.material')} ${t('alert.success.create')}`
		);
		onClose(true);
	};

	const onEdit = async () => {
		const response = await MaterialService.edit(
			{ ...data, client: { id: Number(accountId) } },
			setError
		);
		if (!response) return;
		NotificationToast.Success(
			`${t('port_management.material')} ${t('alert.success.update')}`
		);
		onClose(true);
	};

	const handleInput = (
		e: React.ChangeEvent<HTMLInputElement> | EditableChangeEvent
	) => {
		setModalProps((prev) => ({
			...prev,
			data: {
				...prev.data,
				[e.target.name]: e.target.value,
			},
		}));
	};

	const renderToggles = () => (
		<div className="grid">
			<Toggle
				name="isActive"
				value={data.isActive}
				label={t('status.active')}
				onChange={handleInput}
				disabled={mode === 'view'}
			/>
		</div>
	);

	const renderInputs = () => (
		<div className="grid gap-x-2 gap-y-3">
			<Input
				value={data.name}
				name="name"
				label={t('misc.name')}
				placeholder={t('misc.name')}
				onChange={handleInput}
				disabled={mode === 'view'}
				required
			/>
			<Input
				value={data.value || ''}
				name="value"
				label={t('misc.quantity')}
				placeholder={t('misc.quantity')}
				onChange={handleInput}
				disabled={mode === 'view'}
				type="number"
				required
			/>
			<Input
				value={data.price || ''}
				name="price"
				label={t('misc.price')}
				placeholder={t('misc.price')}
				onChange={handleInput}
				disabled={mode === 'view'}
				type="number"
				required
			/>
		</div>
	);

	return (
		<Modal<IMaterial>
			className=""
			onClose={onClose}
			data={data}
			mode={mode}
			onConfirm={onSubmit}
			isModalVisible={isModalVisible}
			{...props}
		>
			{renderToggles()}
			{renderInputs()}
		</Modal>
	);
};

export default MaterialsModal;
